import { useCallback, useMemo } from 'react';
import { TableWithHeader } from './shared/TableWithHeader';
import { DataTableRow } from '../../common/table/TableTypes';
import {
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { TableTypography } from '../../common/table/utils';
import { translateTypeToHumanName } from '../helpers';
import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { createStableLabelWrapper, markStyles } from '../dependencies/search/LabelWrapperComponent';
import { PossibleSectionInDependencies } from '../dependencies/search/types';
import { ConfigurationItem } from '../../parser/ParserTypes';

const columns = [
  { field: 'label', headerName: 'Label', width: '160px' },
  { field: 'parentType', headerName: 'Type', width: '100px' },
  { field: 'objectName', headerName: 'Object', width: '90px' },
];

const renderRows = ({
  items,
  ObjectNameLabelWrapper,
  section = 'dependencies',
}: {
  items: ConfigurationItem[];
  ObjectNameLabelWrapper: ReturnType<typeof createStableLabelWrapper>;
  section?: PossibleSectionInDependencies;
}) => {
  return items.map((row, index) => {
    return {
      id: row.id,
      name: row.name,
      label: (
        <TableTypography>
          <TableItemHighlightedText
            text={row.label}
            field="label"
            section={section}
            tableType="relatedObjects"
            rowIndex={index}
          />
        </TableTypography>
      ),
      parentType: (
        <TableTypography>
          <TableItemHighlightedText
            text={translateTypeToHumanName(row.parentType)}
            field="parentType"
            section={section}
            tableType="relatedObjects"
            rowIndex={index}
          />
        </TableTypography>
      ),
      objectName: row.objectApiNames?.[0] ? (
        <ObjectTypeLabelAndKeyTag
          objectType={row.objectApiNames?.[0]}
          LabelWrapperComponent={ObjectNameLabelWrapper}
          labelWrapperComponentSx={markStyles}
          labelWrapperComponentProps={{ rowIndex: index }}
        />
      ) : (
        <TableTypography>N/A</TableTypography>
      ),
      _parentType: row.parentType,
      dependencyType: 'fieldType' in row ? (row.fieldType ?? '') : '',
      isClickable: true,
    };
  });
};

interface RelatedObjectsTableProps {
  items: ConfigurationItem[];
  onItemClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
}

export const RelatedObjectsTable = ({
  items,
  onItemClick,
  clearHistory,
  section = 'dependencies',
}: RelatedObjectsTableProps) => {
  // Create stable wrapper component ONCE per table, not per row
  const ObjectNameLabelWrapper = useMemo(
    () =>
      createStableLabelWrapper({
        field: 'objectApiNames',
        section,
        tableType: 'relatedObjects',
      }),
    [section],
  );

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onItemClick({
        id: row.id,
        name: row._nameStr,
        parentType: row._parentType,
        dependencyType: row.dependencyType,
        clearHistory,
        contentType: DependenciesContentViewType.default,
      });
    },
    [onItemClick, clearHistory],
  );

  return (
    <TableWithHeader
      tableHeader="Related Objects"
      columns={columns}
      rows={renderRows({ items, ObjectNameLabelWrapper, section })}
      onRowClick={onRowClick}
      dataTableType="relatedObjects"
      section={section}
    />
  );
};
